import { clientEnv } from 'config/env';
import { createValidationDataObject } from 'app/utilities/form-validation';
import { ENDPOINTS } from 'config/api';
import fetch from 'app/utilities/fetch';
import Icon from 'app/components/partials/icon';
import React, { useEffect, useRef, useState } from 'react';
import {
    SUBMIT_FAILURE_STATUS,
    SUBMIT_SUCCESS_STATUS,
} from 'app/components/partials/enquiry-form';


const EMAIL_REGEX =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const validateEmail = (value) => {
    if (!value || !String(value).trim()) {
        return createValidationDataObject(false, 'Email is required');
    }
    if (!EMAIL_REGEX.test(value.trim())) {
        return createValidationDataObject(
            false,
            'Please enter a valid email address',
        );
    }

    return createValidationDataObject();
};

const validateEmailConfirm = (value, email) => {
    if (!value || !String(value).trim()) {
        return createValidationDataObject(false, 'Email is required');
    }
    if (value.trim() !== email.trim()) {
        return createValidationDataObject(false, 'Emails do not match');
    }

    return createValidationDataObject();
};

const validateRequired = (value, fieldName) => {
    if (!value || !String(value).trim()) {
        return createValidationDataObject(false, `${fieldName} is required`);
    }

    return createValidationDataObject();
};

const INITIAL_FORM_VALUES = {
    email: '',
    email_confirmation: '',
    firstName: '',
    lastName: '',
};

const NewsletterSignupBox = () => {
    const dialogRef = useRef(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [inlineEmail, setInlineEmail] = useState('');
    const [formValues, setFormValues] = useState(INITIAL_FORM_VALUES);
    const [responseStatus, setResponseStatus] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({
        email: null,
        email_confirmation: null,
        firstName: null,
        lastName: null,
    });
    useEffect(() => {
        if (isModalOpen) {
            document.body.classList.add('is-fixed');
        } else {
            document.body.classList.remove('is-fixed');
        }
    }, [isModalOpen]);

    const openModal = () => {
        setFormValues((prev) => ({ ...prev, email: inlineEmail }));
        setFieldErrors({
            email: null,
            email_confirmation: null,
            firstName: null,
            lastName: null,
        });
        setResponseStatus(null);
        setIsModalOpen(true);
        if (dialogRef.current) {
            dialogRef.current.showModal();
        }
    };

    const closeModal = () => {
        setFormValues(INITIAL_FORM_VALUES);
        if (dialogRef.current) {
            dialogRef.current.close();
        }
        setIsModalOpen(false);
    };

    const setField = (name, value) => {
        setFormValues((prev) => ({ ...prev, [name]: value }));
        if (fieldErrors[name]) {
            setFieldErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const runValidation = () => {
        const emailValidation = validateEmail(formValues.email);
        const emailConfirmValidation = validateEmailConfirm(
            formValues.email_confirmation,
            formValues.email,
        );
        const firstNameValidation = validateRequired(
            formValues.firstName,
            'First name',
        );
        const lastNameValidation = validateRequired(
            formValues.lastName,
            'Last name',
        );

        const errors = {
            email: emailValidation.message,
            email_confirmation: emailConfirmValidation.message,
            firstName: firstNameValidation.message,
            lastName: lastNameValidation.message,
        };

        setFieldErrors(errors);

        return (
            emailValidation.valid &&
            emailConfirmValidation.valid &&
            firstNameValidation.valid &&
            lastNameValidation.valid
        );
    };

    const submitForm = (values, token) => {
        values.captchaToken = token;
        setIsSubmitting(true);
        fetch(ENDPOINTS.CAPTURE_CAMPAIGN, null, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(values),
        })
            .then(() => {
                setResponseStatus(SUBMIT_SUCCESS_STATUS);
                setFormValues(INITIAL_FORM_VALUES);
                setInlineEmail('');
            })
            .catch(() => {
                setResponseStatus(SUBMIT_FAILURE_STATUS);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const handleConfirm = () => {
        if (!runValidation()) return;
        grecaptcha.ready(function() {
            grecaptcha
                .execute(clientEnv.RECAPTCHA_KEY, { action: 'submit' })
                .then(function(token) {
                    submitForm(formValues, token);
                })
                .catch(() => {
                    setResponseStatus(SUBMIT_FAILURE_STATUS);
                });
        });
    };

    return (
        <>
            <section
                className={'newsletter-banner || content-section || constrain-width || no-pad'}
            >
                <div className="newsletter-banner-block">
                    <div className="newsletter-banner-details">
                        <h2 className="bold-h2">Don&apos;t miss out!</h2>
                        <span>Sign up for our newsletter to get the latest news and updates.</span>
                    </div>
                    <div className="newsletter-banner-signup">
                        <input
                            id="email"
                            type="email"
                            className="newsletter-banner-signup-email"
                            placeholder="Enter your email"
                            value={inlineEmail}
                            onChange={(e) => setInlineEmail(e.target.value)}
                        />

                        <button
                            type="button"
                            className="newsletter-banner-signup-button button primary"
                            onClick={openModal}
                        >
                            Sign up
                        </button>
                    </div>
                </div>
            </section>

            <dialog
                ref={dialogRef}
                className="newsletter-dialog"
                onClose={() => setIsModalOpen(false)}
                onClick={(e) => {
                    if (e.target === dialogRef.current) {
                        closeModal();
                    }
                }}
                onKeyDown={(e) => e.key === 'Escape' && closeModal()}
            >
                <button
                    className="icon-button || close-button || newsletter-dialog-close-button"
                    onClick={closeModal}
                >
                    <Icon name="close" />
                </button>
                {responseStatus === null && (
                    <div className="newsletter-dialog-content">
                        <h2>Be in the know</h2>
                        <p>Sign up to our newsletter to stay up to date</p>
                        <form
                            className="newsletter-dialog-content-form form"
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleConfirm();
                            }}
                            noValidate
                        >
                            <div className="newsletter-dialog-content-form-inputs">
                                <fieldset
                                    className="newsletter-dialog-content-form-inputs-fieldset"
                                    disabled={isSubmitting}
                                >
                                    <div className="newsletter-dialog-content-form-inputs-fieldset-field">
                                        <input
                                            type="email"
                                            className={`newsletter-dialog-input ${fieldErrors.email ? 'has-error' : ''}`}
                                            name="email"
                                            id="modal-email"
                                            placeholder="Enter your email"
                                            value={formValues.email}
                                            onChange={(e) => setField('email', e.target.value)}
                                        />
                                        {fieldErrors.email && (
                                            <span className="newsletter-dialog-content-form-inputs-fieldset-field-error">
                                                {fieldErrors.email}
                                            </span>
                                        )}
                                    </div>

                                    <div className="newsletter-dialog-content-form-inputs-fieldset-field">
                                        <input
                                            type="email"
                                            className={`newsletter-dialog-input ${fieldErrors.email_confirmation ? 'has-error' : ''}`}
                                            name="email_confirmation"
                                            id="email_confirmation"
                                            placeholder="Confirm your email"
                                            value={formValues.email_confirmation}
                                            onChange={(e) =>
                                                setField('email_confirmation', e.target.value)
                                            }
                                        />
                                        {fieldErrors.email_confirmation && (
                                            <span className="newsletter-dialog-content-form-inputs-fieldset-field-error">
                                                {fieldErrors.email_confirmation}
                                            </span>
                                        )}
                                    </div>

                                    <div className="newsletter-dialog-content-form-inputs-fieldset-field">
                                        <input
                                            type="text"
                                            className={`newsletter-dialog-input ${fieldErrors.firstName ? 'has-error' : ''}`}
                                            name="firstName"
                                            id="firstName"
                                            placeholder="First Name"
                                            value={formValues.firstName}
                                            onChange={(e) => setField('firstName', e.target.value)}
                                        />
                                        {fieldErrors.firstName && (
                                            <span
                                                id="error-firstName"
                                                className="newsletter-dialog-content-form-inputs-fieldset-field-error"
                                            >
                                                {fieldErrors.firstName}
                                            </span>
                                        )}
                                    </div>

                                    <div className="newsletter-dialog-content-form-inputs-fieldset-field">
                                        <input
                                            type="text"
                                            className={`newsletter-dialog-input ${fieldErrors.lastName ? 'has-error' : ''}`}
                                            name="lastName"
                                            id="lastName"
                                            placeholder="Last Name"
                                            value={formValues.lastName}
                                            onChange={(e) => setField('lastName', e.target.value)}
                                        />
                                        {fieldErrors.lastName && (
                                            <span
                                                id="error-lastName"
                                                className="newsletter-dialog-content-form-inputs-fieldset-field-error"
                                            >
                                                {fieldErrors.lastName}
                                            </span>
                                        )}
                                    </div>
                                </fieldset>
                            </div>
                            <p className="newsletter-dialog-content-privacy-policy">
                                Read our <a href="/privacy-policy">Privacy Policy</a> and <a href="/terms-and-conditions">Terms of Service</a>.
                            </p>
                            <div className="newsletter-dialog-content-actions">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="newsletter-dialog-content-actions-button button primary"
                                >
                                    {isSubmitting ? 'Submitting...' : 'Confirm'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
                {responseStatus === SUBMIT_SUCCESS_STATUS && (
                    <div className="newsletter-dialog-response-messages">
                        <h2>Thank you for signing up to our newsletter!</h2>
                    </div>
                )}
                {responseStatus === SUBMIT_FAILURE_STATUS && (
                    <div className="newsletter-dialog-response-messages">
                        <h2>Sorry, we didn&apos;t get your details. Please try again later.</h2>
                    </div>
                )}
            </dialog>
        </>
    );
};

export default NewsletterSignupBox;
